import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const CartContext = createContext();

const CART_KEY = 'ar_menu_cart';
const CART_SLUG_KEY = 'ar_menu_cart_slug';

function loadCart() {
    try {
        const raw = localStorage.getItem(CART_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function saveCart(items) {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
    const [items, setItems] = useState(loadCart);
    const [slug, setSlug] = useState(() => localStorage.getItem(CART_SLUG_KEY) || '');
    const slugRef = useRef(slug);

    useEffect(() => { slugRef.current = slug; }, [slug]);
    useEffect(() => { saveCart(items); }, [items]);
    useEffect(() => { localStorage.setItem(CART_SLUG_KEY, slug); }, [slug]);

    const addItem = useCallback((dish, menuSlug) => {
        if (menuSlug && menuSlug !== slugRef.current) {
            setItems([]);
            setSlug(menuSlug);
        } else if (menuSlug) {
            setSlug(menuSlug);
        }
        setItems(prev => {
            const existing = prev.find(i => i.id === dish.id);
            if (existing) return prev.map(i => i.id === dish.id ? { ...i, qty: i.qty + 1 } : i);
            return [...prev, { id: dish.id, name: dish.name, price: dish.price, image_url: dish.image_url || '', qty: 1 }];
        });
    }, []);

    const updateQty = useCallback((id, delta) => {
        setItems(prev => prev.map(i => {
            if (i.id !== id) return i;
            const newQty = i.qty + delta;
            return newQty > 0 ? { ...i, qty: newQty } : i;
        }).filter(i => i.qty > 0));
    }, []);

    const removeItem = useCallback((id) => {
        setItems(prev => prev.filter(i => i.id !== id));
    }, []);

    const clearCart = useCallback(() => { setItems([]); }, []);

    const totalItems = items.reduce((s, i) => s + i.qty, 0);
    const totalPrice = items.reduce((s, i) => s + i.price * i.qty, 0);

    return (
        <CartContext.Provider value={{ items, slug, totalItems, totalPrice, addItem, updateQty, removeItem, clearCart }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
